import { Button, Input, Pagination, Space, Spin, Tooltip, Typography } from "antd";
import { DownCircleOutlined, SortAscendingOutlined, UpCircleOutlined } from "@ant-design/icons";
import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";

import { CloseOutlined } from "@ant-design/icons";
import styles from "@components/layout/Left.module.scss";

export interface LeftProps {
    title?: string;
    canOrder?: boolean;
    action?: React.ReactNode;
    loadingItems?: boolean;
    items?: any[];
    pagination?: any;
    renderItem?: (item: any) => React.ReactNode;
    height?: string | number;
    onPageChange?: (page: any) => void;
    onSearch?: (value: string) => void;
    searchValue?: string;
    onSaveOrder?: (items: any) => Promise<void>;
}

const Left: React.FC<LeftProps> = ({
    action,
    canOrder = false,
    loadingItems = false,
    renderItem,
    title,
    height,
    onSearch,
    searchValue,
    items,
    pagination,
    onPageChange,
    onSaveOrder
}) => {
    const [localItems, setItems] = useState<any>([]);
    const [needToSaveOrder, toggleNeedToSaveOrder] = useState<boolean>(false);
    const [order, toggleOrdering] = useState<boolean>(false);
    const [savingOrder, toggleSavingOrder] = useState<boolean>(false);

    const style: CSSProperties = useMemo(() => {
        const _styles: CSSProperties = {};

        if (height !== undefined) {
            _styles.height = height;
        }

        return _styles;
    }, [height]);

    const getDefaultItemsState = useCallback(() => {
        setItems(items);
    }, [items]);

    const handleToggleCanOrder = () => {
        if (order && needToSaveOrder) {
            toggleNeedToSaveOrder(false);
            getDefaultItemsState();
        }

        toggleOrdering(!order);
    };

    const handlePutUp = (i: number) => {
        const _items = [...localItems];
        const temp = _items[i];
        _items[i] = _items[i - 1];
        _items[i - 1] = temp;
        setItems(_items);
        toggleNeedToSaveOrder(true);
    };

    const handlePutDown = (i: number) => {
        const _items = [...localItems];
        const temp = _items[i];
        _items[i] = _items[i + 1];
        _items[i + 1] = temp;
        setItems(_items);
        toggleNeedToSaveOrder(true);
    };

    const handleSaveOrder = async () => {
        if (onSaveOrder) {
            toggleSavingOrder(true);
            await onSaveOrder(localItems);
            toggleSavingOrder(false);
            toggleOrdering(false);
        } else {
            throw Error("Missing async method onSaveOrder");
        }
    };

    useEffect(() => {
        if (items) {
            getDefaultItemsState();
        }
    }, [items, getDefaultItemsState]);

    return (
        <div className={styles.left} style={style}>
            <div className={styles.header}>
                <div className={styles.top}>
                    {title && <h2>{title}</h2>}
                    {action && (
                        <div className={styles.actions}>
                            <div>
                                {order ? (
                                    <div>
                                        {needToSaveOrder && (
                                            <Button
                                                loading={savingOrder}
                                                size="small"
                                                onClick={handleSaveOrder}
                                            >
                                                Save order
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    action
                                )}
                            </div>
                            {canOrder && (
                                <Tooltip
                                    placement="top"
                                    title={`${order ? "Disable" : "Enable"} order`}
                                >
                                    <Button
                                        size="small"
                                        type={order ? "primary" : "default"}
                                        onClick={handleToggleCanOrder}
                                        icon={<SortAscendingOutlined />}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
                <div>
                    {onSearch && searchValue !== undefined && (
                        <Input
                            type="text"
                            value={searchValue}
                            onChange={(e) => onSearch(e.target.value)}
                            placeholder="Enter a search input"
                            size="small"
                            allowClear
                            className={styles.search}
                        />
                    )}
                </div>
            </div>
            <Spin spinning={loadingItems}>
                <div className={styles.items}>
                    {localItems?.map(
                        (item: any, i: number) =>
                            renderItem && (
                                <div className={styles.listItem} key={i}>
                                    <div className={styles.item}>{renderItem(item)}</div>
                                    {order && (
                                        <div className={styles.orderIcons}>
                                            <div className={styles.orderIconWrapper}>
                                                {i !== 0 && (
                                                    <UpCircleOutlined
                                                        className={styles.orderIcon}
                                                        onClick={() => handlePutUp(i)}
                                                    />
                                                )}
                                            </div>
                                            <div className={styles.orderIconWrapper}>
                                                {i !== localItems.length - 1 && (
                                                    <DownCircleOutlined
                                                        className={styles.orderIcon}
                                                        onClick={() => handlePutDown(i)}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                    )}
                    {!loadingItems && localItems.length === 0 && (
                        <Space direction="vertical" align="center">
                            <Typography.Text type="secondary">This list is empty</Typography.Text>
                        </Space>
                    )}
                </div>
            </Spin>
            {pagination && (
                <div className={styles.pagination}>
                    <Pagination
                        simple
                        defaultCurrent={pagination.current}
                        total={pagination.total}
                        pageSize={pagination.perPage}
                        onChange={onPageChange}
                    />
                </div>
            )}
        </div>
    );
};

export default Left;
